import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight, Lock, Eye, FileCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import SEO from '../components/SEO';

const SecuritySolutions = () => {
  const services = [
    {
      icon: Lock,
      title: 'Security Best Practices',
      description: 'Industry-standard security measures and compliance protocols implementation'
    },
    {
      icon: FileCheck,
      title: 'Compliance Management',
      description: 'HIPAA, SOC 2, and other regulatory compliance standards'
    },
    {
      icon: Eye,
      title: '24/7 Security Monitoring',
      description: 'Continuous security monitoring and threat detection systems'
    },
    {
      icon: AlertTriangle,
      title: 'Threat Response',
      description: 'Rapid incident response and threat mitigation protocols'
    }
  ];

  const benefits = [
    '94% improved security post-migration',
    'HIPAA-compliant infrastructure',
    'SOC 2 compliance support',
    'Continuous threat monitoring',
    'Automated security updates',
    'Incident response protocols'
  ];

  const complianceStandards = [
    { name: 'HIPAA', description: 'Healthcare data protection compliance' },
    { name: 'SOC 2', description: 'Service organization control standards' },
    { name: 'GDPR', description: 'General Data Protection Regulation' },
    { name: 'ISO 27001', description: 'Information security management' }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Cloud Security & Compliance — HIPAA Compliant | NIVA Digital AI"
        description="Comprehensive cloud security and compliance services including HIPAA compliance, vulnerability management, security audits, and threat detection."
        keywords="cloud security, HIPAA compliant cloud, security compliance, vulnerability management, cloud threat detection, security audits"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/services/security`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield size={20} />
                <span>Security Solutions</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Enterprise-Grade
                <span className="block text-red-600 mt-2">Security</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Comprehensive security infrastructure with best practices, compliance standards, and 24/7 monitoring to protect your business.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Get Started <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-red-100 to-orange-50 rounded-2xl p-8 shadow-2xl">
                <Shield className="text-red-600 mx-auto" size={200} strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Services */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Security Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Multi-layered security approach to protect your infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-red-600" size={28} />
                    </div>
                    <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Success Stats */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Proven Security Excellence
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-2 border-red-600 text-center">
              <CardContent className="pt-8">
                <p className="text-5xl font-bold text-red-600 mb-2">94%</p>
                <p className="text-gray-600 text-lg">Improved Security</p>
                <p className="text-sm text-gray-500 mt-2">Post cloud migration</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-600 text-center">
              <CardContent className="pt-8">
                <p className="text-5xl font-bold text-red-600 mb-2">24/7</p>
                <p className="text-gray-600 text-lg">Monitoring</p>
                <p className="text-sm text-gray-500 mt-2">Continuous protection</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-600 text-center">
              <CardContent className="pt-8">
                <p className="text-5xl font-bold text-red-600 mb-2">100%</p>
                <p className="text-gray-600 text-lg">Compliance</p>
                <p className="text-sm text-gray-500 mt-2">Regulatory standards met</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Standards */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Compliance Standards
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Infrastructure engineered to meet and adhere to established compliance standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {complianceStandards.map((standard, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 text-center">
                <CardContent className="pt-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="text-red-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{standard.name}</h3>
                  <p className="text-gray-600 text-sm">{standard.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Healthcare Focus */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                HIPAA-Compliant Infrastructure
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Specialized expertise in building healthcare-compliant cloud infrastructure with centralized patient data management and EHR system integration.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">HIPAA Compliance</h3>
                    <p className="text-gray-600">Full healthcare data protection and privacy compliance</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">EHR Integration</h3>
                    <p className="text-gray-600">Seamless Electronic Health Records system integration</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Data Security</h3>
                    <p className="text-gray-600">Encrypted data storage and secure access controls</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-red-100 to-orange-50 rounded-2xl p-12 shadow-2xl">
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <Lock className="text-red-600 mb-2" size={32} />
                    <h4 className="font-semibold">Encrypted</h4>
                    <p className="text-sm text-gray-600 mt-1">End-to-end encryption</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <FileCheck className="text-red-600 mb-2" size={32} />
                    <h4 className="font-semibold">Compliant</h4>
                    <p className="text-sm text-gray-600 mt-1">Healthcare standards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Secure Your Infrastructure Today
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get a comprehensive security assessment and consultation
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SecuritySolutions;